import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Battery, Brain, Activity, Zap, CheckCircle2, XCircle, Clock, ShieldCheck, ChevronDown, ArrowRight, Lock, Microscope, Coffee } from 'lucide-react';
import { Footer } from './components/Footer';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const faqs = [{
    q: '¿Es seguro tomar estos nutracéuticos a diario?',
    a: 'Sí, el protocolo utiliza sustancias con amplio margen de seguridad y respaldo científico en dosis clínicas.'
  }, {
    q: '¿Necesito comprar suplementos caros?',
    a: 'Te enseñamos a elegir por biodisponibilidad. A veces, la forma más barata es la que mejor funciona si se toma en el momento correcto.'
  }, {
    q: '¿Puedo hacerlo si trabajo de noche?',
    a: 'Sí, incluimos una adaptación del protocolo para turnos rotativos y guardias médicas.'
  }, {
    q: '¿Contiene estimulantes?',
    a: 'No. El objetivo es que tu cuerpo produzca su propia energía, no darte un subidón artificial.'
  }, {
    q: '¿Cuánto tardaré en sentir resultados?',
    a: 'El enfoque cerebral es casi inmediato (45 min); la reparación adrenal toma entre 14 y 21 días.'
  }];
  return <div className="min-h-screen bg-slate-950 text-slate-300 selection:bg-cyan-500/30" data-id="element-1">
      {/* Navigation / Top Bar */}
      <div className="w-full bg-slate-900/50 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50" data-id="element-2">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center" data-id="element-3">
          <div className="flex items-center gap-2" data-id="element-4">
            <Activity className="w-6 h-6 text-cyan-400" data-id="element-5" />
            <span className="font-display font-bold text-white tracking-wider" data-id="element-6">
              BIO-HACK 24H
            </span>
          </div>
          <a href="#checkout" className="text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors" data-id="element-7">
            Obtener Acceso
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden" data-id="element-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-slate-950 to-slate-950" data-id="element-9"></div>

        <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-11">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-6" data-id="element-13">
                <Zap className="w-4 h-4" data-id="element-14" />
                <span data-id="element-15">Balance Biológico</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-16">
                PROTOCOLO BIO-HACK 24H: <br data-id="element-17" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500" data-id="element-18">
                  EL FIN DE LA FATIGA CRÓNICA Y LA NIEBLA MENTAL
                </span>
              </h1>

              <p className="text-xl text-slate-400 mb-8 leading-relaxed" data-id="element-19">
                Recupera tu claridad cognitiva y restaura tu energía celular
                mediante la sincronización nutracéutica de grado médico, sin
                depender de estimulantes ni cafeína.
              </p>

              <a href="#checkout" className="inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-lg px-8 py-4 rounded-xl transition-all hover:scale-105 hover:shadow-[0_0_30px_-5px_rgba(6,182,212,0.4)] w-full sm:w-auto" data-id="element-20">
                DESCARGAR PROTOCOLO AHORA
                <ArrowRight className="w-5 h-5" data-id="element-21" />
              </a>
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-500" data-id="element-22">
                <ShieldCheck className="w-4 h-4" data-id="element-23" />
                <span data-id="element-24">Pago seguro y acceso instantáneo</span>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-25">
              <div className="absolute inset-0 bg-cyan-500/20 blur-[100px] rounded-full" data-id="element-26"></div>
              <img src="https://cdn.magicpatterns.com/uploads/7zui8NmiMcRH6ojJFzx9t9/ChatGPT_Image_28_jun_2026,_10_58_45.png" alt="Protocolo Bio-Hack 24H en múltiples dispositivos" className="relative z-10 w-full h-auto rounded-2xl shadow-2xl border border-slate-800" data-id="element-27" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Hook & Problem */}
      <section className="py-24 bg-slate-900/50 border-y border-slate-800" data-id="element-28">
        <div className="max-w-4xl mx-auto px-6" data-id="element-29">
          <FadeIn data-id="element-30">
            <div className="prose prose-invert prose-lg max-w-none" data-id="element-31">
              <p className="text-2xl leading-relaxed text-slate-300 font-medium mb-8" data-id="element-32">
                Si te despiertas sintiéndote más cansado que cuando te
                acostaste, no es falta de voluntad.{' '}
                <strong className="text-white" data-id="element-33">
                  Es una disfunción de tu biología celular.
                </strong>
              </p>
              <p className="text-slate-400" data-id="element-34">
                Estás viviendo en "modo ahorro". Tu cuerpo está procesando el
                estrés a una velocidad mayor de la que puede repararse, y tu
                cerebro está pagando el precio con lagunas mentales, falta de
                enfoque y un agotamiento que ningún café puede solucionar.
              </p>

              <div className="my-12 p-8 bg-slate-950 rounded-2xl border border-red-900/30 relative overflow-hidden" data-id="element-35">
                <div className="absolute top-0 left-0 w-1 h-full bg-red-500" data-id="element-36"></div>
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3" data-id="element-37">
                  <Battery className="w-6 h-6 text-red-500" data-id="element-38" />
                  La trampa de la "Normalidad"
                </h3>
                <p className="text-slate-400 mb-4" data-id="element-39">
                  La medicina tradicional suele ignorar lo que ocurre entre el
                  "estás sano" y el "estás enfermo". Te dicen que tus análisis
                  son normales, pero tú sientes que tu cerebro funciona al 50%.
                </p>
                <p className="text-slate-400" data-id="element-40">
                  El agotamiento de las glándulas adrenales, el estrés oxidativo
                  en la mitocondria y la inflamación de bajo grado están
                  bloqueando tu capacidad de producir ATP (energía). Mientras
                  sigas tratando de "forzar" el sistema con energía prestada de
                  la cafeína, solo estás acelerando el colapso final.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why What You Tried Didn't Work */}
      <section className="py-24" data-id="element-41">
        <div className="max-w-6xl mx-auto px-6" data-id="element-42">
          <FadeIn data-id="element-43">
            <div className="text-center mb-16" data-id="element-44">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-45">
                Por qué lo que has intentado no funciona
              </h2>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto" data-id="element-46">
                Has estado tratando síntomas, no la raíz metabólica.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-47">
            {[{
            icon: <Microscope className="w-6 h-6 text-orange-400" data-id="element-48" />,
            title: 'Multivitamínicos Genéricos',
            desc: 'No tienen la biodisponibilidad necesaria para cruzar la barrera hematoencefálica.'
          }, {
            icon: <Coffee className="w-6 h-6 text-orange-400" data-id="element-49" />,
            title: 'Exceso de Cafeína',
            desc: 'Agota aún más tus reservas de cortisol, empeorando la fatiga adrenal a largo plazo.'
          }, {
            icon: <Clock className="w-6 h-6 text-orange-400" data-id="element-50" />,
            title: 'Descanso Pasivo',
            desc: 'Dormir no repara la mitocondria dañada; se necesita una intervención bioquímica específica.'
          }, {
            icon: <Zap className="w-6 h-6 text-orange-400" data-id="element-51" />,
            title: 'Estimulantes Químicos',
            desc: "Causan un 'crash' inevitable que nubla tu juicio al final del día."
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-52">
                <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-2xl h-full flex gap-4" data-id="element-53">
                  <div className="shrink-0 mt-1" data-id="element-54">{item.icon}</div>
                  <div data-id="element-55">
                    <h4 className="text-xl font-bold text-white mb-2" data-id="element-56">
                      {item.title}
                    </h4>
                    <p className="text-slate-400" data-id="element-57">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* The Unique Mechanism */}
      <section className="py-24 bg-cyan-950/20 border-y border-cyan-900/30 relative overflow-hidden" data-id="element-58">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-cyan-500/5 blur-[120px] pointer-events-none" data-id="element-59"></div>
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-60">
          <FadeIn data-id="element-61">
            <Activity className="w-12 h-12 text-cyan-400 mx-auto mb-6" data-id="element-62" />
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8" data-id="element-63">
              La Sincronización Celular 24h
            </h2>
            <p className="text-xl text-slate-300 leading-relaxed mb-8" data-id="element-64">
              Este protocolo no se limita a sugerir suplementos; organiza los
              nutracéuticos según tu ritmo circadiano y tus rutas metabólicas.
            </p>
            <p className="text-lg text-slate-400 leading-relaxed" data-id="element-65">
              Al administrar el compuesto exacto en el momento en que tu cuerpo
              lo necesita para reparar o activar, reseteamos el eje HPA y
              optimizamos la producción de neurotransmisores como la dopamina y
              la acetilcolina.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24" data-id="element-66">
        <div className="max-w-6xl mx-auto px-6" data-id="element-67">
          <FadeIn data-id="element-68">
            <div className="text-center mb-16" data-id="element-69">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-70">
                Cómo funciona el protocolo
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-71">
            {[{
            step: '01',
            title: 'Fase de Reset',
            desc: 'Limpiamos los receptores y estabilizamos el cortisol.'
          }, {
            step: '02',
            title: 'Fase de Carga',
            desc: 'Reparamos la función mitocondrial con precursores específicos.'
          }, {
            step: '03',
            title: 'Fase de Optimización',
            desc: 'Implementamos el stack de enfoque para rendimiento cognitivo superior.'
          }].map((phase, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-72">
                <div className="relative p-8 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-cyan-500/50 transition-colors group" data-id="element-73">
                  <div className="text-5xl font-display font-bold text-slate-800 mb-6 group-hover:text-cyan-900/50 transition-colors" data-id="element-74">
                    {phase.step}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-75">
                    {phase.title}
                  </h3>
                  <p className="text-slate-400" data-id="element-76">{phase.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-24 bg-slate-900/30" data-id="element-77">
        <div className="max-w-4xl mx-auto px-6" data-id="element-78">
          <FadeIn data-id="element-79">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center" data-id="element-80">
              Todo lo que incluye tu acceso
            </h2>
            <div className="space-y-4" data-id="element-81">
              {[{
              title: 'Manual Bio-Hack 24h',
              desc: 'La hoja de ruta completa para restaurar tu energía desde la célula.'
            }, {
              title: 'Protocolo de Rescate Adrenal',
              desc: 'Cómo usar adaptógenos para eliminar el cansancio crónico.'
            }, {
              title: 'Plan de Neuro-Optimización',
              desc: 'El stack exacto para estudiantes y profesionales que necesitan enfoque láser en exámenes o guardias.'
            }, {
              title: 'Mapa de Crono-Nutrición',
              desc: 'Qué tomar, cuánto tomar y a qué hora exacta para maximizar resultados.'
            }, {
              title: 'Checklist de Implementación de 7 Días',
              desc: 'Para pasar de la niebla mental a la claridad total.'
            }].map((item, i) => <div key={i} className="flex items-start gap-4 p-6 bg-slate-950 rounded-xl border border-slate-800" data-id="element-82">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400 shrink-0 mt-1" data-id="element-83" />
                  <div data-id="element-84">
                    <h4 className="text-lg font-bold text-white mb-1" data-id="element-85">
                      {item.title}
                    </h4>
                    <p className="text-slate-400" data-id="element-86">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Proof / Believability */}
      <section className="py-20 bg-cyan-500 text-slate-950" data-id="element-87">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-88">
          <FadeIn data-id="element-89">
            <h2 className="text-3xl font-bold mb-6" data-id="element-90">La Ventana de 7 Días</h2>
            <p className="text-xl font-medium max-w-2xl mx-auto" data-id="element-91">
              En la primera semana de aplicación del protocolo de Sincronización
              Celular, el <strong className="text-2xl font-black" data-id="element-92">85%</strong>{' '}
              de los usuarios reporta la desaparición de la "caída de energía de
              las 3:00 PM" y la recuperación de la capacidad de lectura profunda
              sin distracciones.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Who is this for / not for */}
      <section className="py-24" data-id="element-93">
        <div className="max-w-6xl mx-auto px-6" data-id="element-94">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-95">
            <FadeIn data-id="element-96">
              <div className="p-8 rounded-3xl bg-slate-900/50 border border-slate-800" data-id="element-97">
                <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3" data-id="element-98">
                  <CheckCircle2 className="w-8 h-8 text-cyan-400" data-id="element-99" />
                  Para quién es esto
                </h3>
                <ul className="space-y-6" data-id="element-100">
                  {['Médicos y estudiantes de medicina bajo niveles extremos de estrés y falta de sueño.', 'Profesionales de alto rendimiento que no pueden permitirse un solo día de baja productividad.', 'Personas mayores de 35 años que sienten que su energía ya no es la misma de antes.', 'Emprendedores que necesitan enfoque inquebrantable para tomar decisiones bajo presión.'].map((text, i) => <li key={i} className="flex items-start gap-3 text-slate-300" data-id="element-101">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2.5 shrink-0" data-id="element-102"></div>
                      <span data-id="element-103">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-104">
              <div className="p-8 rounded-3xl bg-slate-900/50 border border-slate-800" data-id="element-105">
                <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3" data-id="element-106">
                  <XCircle className="w-8 h-8 text-red-400" data-id="element-107" />
                  Para quién NO es
                </h3>
                <ul className="space-y-6" data-id="element-108">
                  {["Personas que buscan una 'pastilla mágica' sin leer la fundamentación técnica.", 'Quienes no estén dispuestos a ajustar su horario de consumo de estimulantes.', 'Individuos que buscan consejo médico para enfermedades graves (esto es optimización, no medicina de emergencia).'].map((text, i) => <li key={i} className="flex items-start gap-3 text-slate-400" data-id="element-109">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2.5 shrink-0" data-id="element-110"></div>
                      <span data-id="element-111">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-slate-900/30" data-id="element-112">
        <div className="max-w-3xl mx-auto px-6" data-id="element-113">
          <FadeIn data-id="element-114">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center" data-id="element-115">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-4" data-id="element-116">
              {faqs.map((faq, i) => <div key={i} className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950" data-id="element-117">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left px-6 py-5 flex justify-between items-center hover:bg-slate-900/50 transition-colors" data-id="element-118">
                    <span className="font-medium text-white" data-id="element-119">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-120" />
                  </button>
                  <AnimatePresence data-id="element-121">
                    {openFaq === i && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-122">
                        <div className="px-6 pb-5 text-slate-400 border-t border-slate-800/50 pt-4" data-id="element-123">
                          {faq.a}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Offer + Price Block */}
      <section id="checkout" className="py-32 relative overflow-hidden" data-id="element-124">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-slate-950 to-slate-950" data-id="element-125"></div>
        <div className="max-w-4xl mx-auto px-6 relative z-10" data-id="element-126">
          <FadeIn data-id="element-127">
            <div className="bg-slate-900/80 backdrop-blur-xl border border-cyan-500/30 p-8 md:p-12 rounded-3xl text-center shadow-[0_0_50px_-12px_rgba(6,182,212,0.2)]" data-id="element-128">
              <Brain className="w-16 h-16 text-cyan-400 mx-auto mb-6" data-id="element-129" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-130">
                Empieza a reparar tu biología hoy mismo
              </h2>
              <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto" data-id="element-131">
                Obtén el acceso inmediato al Protocolo Bio-Hack 24h por una
                fracción del costo de una consulta privada de medicina
                funcional.
              </p>

              <div className="mb-10" data-id="element-132">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-sm font-bold uppercase tracking-wider mb-5" data-id="element-133">
                  <Zap className="w-4 h-4" data-id="element-134" />
                  74% de descuento por tiempo limitado
                </div>
                <span className="text-slate-400 uppercase tracking-widest text-sm font-bold block mb-2" data-id="element-135">
                  Inversión Única
                </span>
                <div className="flex items-center justify-center gap-4" data-id="element-136">
                  <span className="text-2xl md:text-3xl font-display font-bold text-slate-500 line-through decoration-red-500/70 decoration-2" data-id="element-137">
                    $27.00
                  </span>
                  <span className="text-6xl font-display font-bold text-white" data-id="element-138">
                    $7.00 <span className="text-2xl text-slate-500" data-id="element-139">USD</span>
                  </span>
                </div>
                <p className="text-cyan-400 font-semibold mt-3" data-id="element-140">
                  Ahorras $20.00 USD hoy
                </p>
              </div>

              <a href="https://novacorpsf.com/cart/45979059060815:1" className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xl px-12 py-5 rounded-2xl transition-all hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.5)]" data-id="element-141">
                DESCARGAR PROTOCOLO AHORA
                <ArrowRight className="w-6 h-6" data-id="element-142" />
              </a>

              <div className="flex items-center justify-center gap-2 mt-6 text-sm text-slate-400" data-id="element-143">
                <Lock className="w-4 h-4" data-id="element-144" />
                <span data-id="element-145">Pago 100% seguro y acceso instantáneo</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <Footer data-id="element-146" />
    </div>;
}